import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { TaskFilter } from "../../types";

export function useUpdateFilterOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      filterId,
      newOrderPosition,
    }: {
      filterId: string;
      newOrderPosition: number;
    }) => {
      const { error } = await supabase
        .from("task_filters")
        .update({
          order_position: newOrderPosition,
          updated_at: new Date().toISOString(),
        })
        .eq("id", filterId);
      if (error) throw error;
    },

    onMutate: async ({ filterId, newOrderPosition }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.filters });

      const prevFilters = queryClient.getQueryData<TaskFilter[]>(queryKeys.filters);

      // Left alone when the cache has not loaded: writing `[]` into it would
      // flip useFilters out of its pending state and drop the app's loading
      // gate, and onError's snapshot guard could not put it back.
      queryClient.setQueryData<TaskFilter[]>(queryKeys.filters, (old) =>
        old
          ? old
              .map((f) =>
                f.id === filterId
                  ? { ...f, order_position: newOrderPosition }
                  : f
              )
              .sort((a, b) => a.order_position - b.order_position)
          : old
      );

      return { prevFilters };
    },

    onError: (err, _, context) => {
      if (context?.prevFilters)
        queryClient.setQueryData<TaskFilter[]>(queryKeys.filters, context.prevFilters);
      Sentry.captureException(err, { extra: { mutation: "useUpdateFilterOrder" } });
      toast.error("Failed to reorder filter. Please try again.");
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.filters });
    },
  });
}
